import { useState, useEffect, useMemo } from 'react';
import ConsentContentContext from '../../context/contentContext';
import './styles.scss';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  httpGetRedirectClrEnrlmnt,
  httpGetVerifyClrEnrlmnt,
} from '../../api/REST_services_meg/verifyClear';
import verifyImage from '../../assets/image.svg';
import Loader from '../mainContainer/loader';
import { useToken } from '../../hooks/useToken';
import {
  getEnforcedUserKey,
  getFromSession,
  storeToSession,
} from '../../Utils/Utils';
import CountrySelection from './countrySelection';
import FooterLinks from './footerLinks';
import { formatData } from './helper';
import { useNavigate } from 'react-router';

const initialUserState = {
  enrollmentStatus: false,
  enforcedEnrollment: false,
  errorMessage: '',
};

const getBackgroundImage = (enrollmentStatus, enforcedEnrollment) => {
  if (enrollmentStatus) {
    return 'verify-complete-bg-img';
  }
  if (enforcedEnrollment) {
    return 'enforced-enrollment-bg-img';
  }
  return 'onload-bg-img';
};

const Identity = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { acquireToken } = useToken();
  const sessionKey = getEnforcedUserKey(instance?.getActiveAccount()?.username);
  const navigate = useNavigate();

  const initUser = useMemo(() => {
    return {
      ...initialUserState,
      enforcedEnrollment: getFromSession(sessionKey, false) === 'true',
    };
  }, []);

  // Define user state and loading state
  const [user, setUser] = useState(initUser);
  const [consentDisclosureContent, setConsentDisclosureContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(
    getBackgroundImage(user.enrollmentStatus, user.enforcedEnrollment)
  );

  // Function to reset user state to initial values
  const resetUserState = () => {
    setUser(initUser);
  };

  useEffect(() => {
    apiCall();
  }, [acquireToken]);

  // Error handler with a single message for all 4xx and 5xx errors, including 401 Unauthorized
  const handleError = (error) => {
    const status = error?.response?.status;
    const errorMessage = 'Something went wrong. Please try again later.';
    if (status === 401) {
      instance.loginRedirect();
    } else if (status === 429) {
      setIsButtonDisabled(true);
      setUser((prevUser) => ({
        ...prevUser,
        errorMessage,
      }));
    } else {
      setUser((prevUser) => ({
        ...prevUser,
        errorMessage,
      }));
    }
  };

  // API call to fetch enrollment status
  const apiCall = async () => {
    if (isAuthenticated) {
      try {
        setLoading(true);
        resetUserState();

        const response = await httpGetVerifyClrEnrlmnt(acquireToken);

        const enrollmentStatus = response?.data?.enrollmentStatus || false;
        const enforcedEnrollment = response?.data?.enforcedEnrollment || false;
        const userMessage = response?.data?.userMessage;
        const clearOptFlow = response?.data?.clearOptFlow || false;
        if(!enrollmentStatus && clearOptFlow){
          navigate('/verification-pending');
          return;
        }
        setUser({
          ...user,
          enrollmentStatus,
          enforcedEnrollment,
          userMessage: userMessage,
        });

        const usableContent = formatData(response?.data);
        setConsentDisclosureContent(usableContent);
        storeToSession(sessionKey, enforcedEnrollment);

        // Update background image based on enforcedEnrollment or enrollmentStatus
        setBackgroundImage(
          getBackgroundImage(enrollmentStatus, enforcedEnrollment)
        );
      } catch (error) {
        console.error('httpGetVerifyClrEnrlmnt submission error:', error);
        handleError(error); // Handle 401 error and others
      } finally {
        setLoading(false);
      }
    }
  };

  const onVerifyClearHandler = async (consent_id) => {
    try {
      setLoading(true);
      setUser({
        ...user,
        errorMessage: undefined,
      });
      const response = await httpGetRedirectClrEnrlmnt(acquireToken, consent_id);
      // const response = await httpGetRedirectClrEnrlmnt(acquireToken);
      const clearRedirectUrl = response?.data?.endpoint;
      window.location.href = clearRedirectUrl;
    } catch (error) {
      console.error('httpGetRedirectClrEnrlmnt submission error:', error);
      handleError(error); // Handle 401 error and others
    } finally {
      setLoading(false);
    }
  };

  // Conditionally render loader or main content based on the loading state
  return (
    <ConsentContentContext.Provider value={consentDisclosureContent}>
    <div
      data-testid="identity-container"
      className={`container identity-main ${backgroundImage}`}
    >
      {loading ? (
        <div className="loader-overlay">
          <Loader />
        </div>
      ) : (
        <>
        {user.errorMessage ? (
              <div className='white-box'>
              <div className="error-message-container">
                <p className="error-message" style={{ color: 'red' }}>
                  {user.errorMessage}
                </p>
              </div>
              </div>
            ) :(
              <>
          {user.enforcedEnrollment && !user.enrollmentStatus && (
            <div className="action-required-message">
              <p>
                ACTION REQUIRED! <br /> In order to restore network access, you
                must complete Identity Verification. Select from below to
                begin the process.
              </p>
            </div>
          )}
          {user.userMessage && (
            <div className='white-box'>
              <div className="error-message-container">
                <p className="error-message" style={{ color: 'red' }}>
                  {user.userMessage}
                </p>
                </div>
              </div>
            )}
          {/* Render the button based on enrollment status */}
          {user.enrollmentStatus ? (
            <div className="white-box">
              <button
                className="verify-button"
                id="verify_button"
                type="submit"
                disabled
                style={{ opacity: 0.4, pointerEvents: 'none' }}
              >
                <img src={verifyImage} alt="Verify with CLEAR" />
                Verify with CLEAR
              </button>
            </div>
          ) : (
            <CountrySelection user={user} onVerifyClearHandler={onVerifyClearHandler}/>
          )}

            {user.enrollmentStatus && (
              <div className="white-box">
              <p
                style={{ fontWeight: 'bold', fontSize: '12px', color: 'green' }}
              >
                {`${
                  instance?.getActiveAccount()?.username
                } successfully verified in CLEAR`}
              </p>
              {/* Outcome box based on enrollment status */}
              <div className="outcome-box-success" id="outcome_box">
                Verified
              </div>
              <FooterLinks user={user} />
              </div>
            )}
          </>
        )}
        </>
      )}
    </div>
    </ConsentContentContext.Provider>
  );
};

export default Identity;
